import { useState } from 'react';
import { Download, ExternalLink, FileText, Paperclip } from 'lucide-react';
import type { Attachment } from '../types/attachment';
import {
  fetchAuthenticatedBlob,
  openAuthenticatedAttachmentInNewTab,
} from '../utils/authenticatedDownload';
import { formatAttachmentByteSize } from '../utils/attachmentValidation';

export interface AttachmentsListViewProps {
  attachments: Attachment[];
  title?: string;
}

const AttachmentsListView = ({
  attachments,
  title = 'Attachments',
}: AttachmentsListViewProps) => {
  const [activeId, setActiveId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (attachments.length === 0) return null;

  const handleOpen = async (attachment: Attachment) => {
    try {
      setActionError(null);
      setActiveId(attachment.id);
      await openAuthenticatedAttachmentInNewTab(attachment.download_url);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : `Could not open ${attachment.filename}.`
      );
    } finally {
      setActiveId(null);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      setActionError(null);
      setActiveId(attachment.id);
      const blob = await fetchAuthenticatedBlob(attachment.download_url);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachment.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      setActionError(`Could not download ${attachment.filename}.`);
    } finally {
      setActiveId(null);
    }
  };

  return (
    <section className="mb-6">
      <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Paperclip className="h-4 w-4 text-gray-500" />
        {title}
      </h2>
      {actionError && <p className="mb-3 text-sm text-red-600">{actionError}</p>}
      <div className="space-y-2">
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3"
          >
            <button
              type="button"
              onClick={() => void handleOpen(attachment)}
              disabled={activeId === attachment.id}
              className="flex items-start gap-2 min-w-0 flex-1 text-left rounded-md hover:bg-white/80 disabled:opacity-60 transition-colors"
              title="Open in new tab"
            >
              <FileText className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p
                  className="text-sm font-medium text-slate-800 truncate hover:underline"
                  title={attachment.filename}
                >
                  {attachment.filename}
                </p>
                <p className="text-xs text-gray-500">
                  {formatAttachmentByteSize(attachment.byte_size)}
                  {attachment.uploaded_by?.name ? ` · ${attachment.uploaded_by.name}` : ''}
                </p>
                {attachment.note && (
                  <p className="text-xs text-gray-600 mt-1">{attachment.note}</p>
                )}
              </div>
            </button>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => void handleOpen(attachment)}
                disabled={activeId === attachment.id}
                className="p-1.5 text-gray-500 hover:text-slate-800 hover:bg-white rounded-md border border-transparent hover:border-gray-200 disabled:opacity-50"
                title="Open in new tab"
              >
                <ExternalLink className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => void handleDownload(attachment)}
                disabled={activeId === attachment.id}
                className="p-1.5 text-gray-500 hover:text-slate-800 hover:bg-white rounded-md border border-transparent hover:border-gray-200 disabled:opacity-50"
                title="Download"
              >
                <Download className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default AttachmentsListView;
